import {
  getDocs,
  getDoc,
  doc,
  updateDoc,
  setDoc,
  where,
  getCountFromServer,
  query,
} from 'firebase/firestore'
import { uniqBy, isEmpty, sortBy } from 'lodash'
import { getUnix } from '@/libs/date-format'
import { constructQuery } from '@core/utils/utils'
import { RevisionEventType } from '@core/revisions/revisions'
import store from '@/store'
import i18n from '@/libs/i18n'
import { getTenantContextInstance as tenantCtx } from '@/plugins/tenant'
import router from '@/router'
import { allowed, FEATURES } from '@/auth'

export default {
  namespaced: true,
  state: {
    list: [],
    templates: [],
    tenantTemplates: [],
    params: {},
    current: {
      connectorsPermissions: {},
      connectorsTriggerConditions: {},
      connectorObjects: {},
    },
    filteredList: [],
    removeModal: {
      show: false,
      data: {},
    },
    node: {
      selected: null,
      created: null,
      edited: null,
      remove: null,
    },
    selected_connector: null,
    new_created_connector: false,
    mapping_connectors_fields: [],
    count: 0,
  },
  getters: {
    params: state => state.params,
    getCurrent: state => state.current,
    getRemoveModal: state => state.removeModal,
    getAll: state => state.list,
    getById: state => id => state.list.filter(el => !el.deletedAt).find(item => item.id === id),
    getFilteredList: state => state.filteredList.map(docRef => ({ ...docRef.data(), id: docRef.id })),
    getCurrentConnectorsMappings: state => {
      const parsed = JSON.parse(state.current.connectorsMappings ?? '{}')
      return parsed
    },
    getCurrentConnectorsInstructions: state => {
      const parsed = JSON.parse(state.current.connectorsInstructions ?? '{}')
      return parsed
    },
    getCurrentConnectorsPermissions: state => connector => (Object.prototype.hasOwnProperty.call(state.current?.connectorsPermissions ?? {}, connector) ? state.current?.connectorsPermissions[connector] ?? {} : {}),
    getCurrentConnectorsTriggerConditions: state => connector => (Object.prototype.hasOwnProperty.call(state.current.connectorsTriggerConditions ?? {}, connector) ? state.current.connectorsTriggerConditions[connector] ?? {} : {}),
    getCurrentConnectorObjects: state => connector => (Object.prototype.hasOwnProperty.call(state.current?.connectorObjects ?? {}, connector) ? state.current?.connectorObjects[connector] ?? {} : {}),
    getCurrentConnectorsPrefillsInstructions: state => {
      const parsed = JSON.parse(state.current.connectorsPrefillsInstructions ?? '{}')
      return parsed
    },
    getMappingConnectorsFields: state => state.mapping_connectors_fields,
    getTemplates: state => {
      const docs = state.templates || []
      const list = docs.map(docRef => {
        if (!docRef) return null
        try {
          if (typeof docRef.data === 'function') {
            const data = docRef.data() || {}
            return { ...data, id: docRef.id }
          }
          if (docRef.id) return { ...docRef }
          return null
        } catch (_) {
          return null
        }
      }).filter(t => t && t.id)
      return sortBy(list, 'category')
    },
    getTenantTemplates: state => {
      const docs = state.tenantTemplates || []
      const list = docs.map(docRef => {
        if (!docRef) return null
        try {
          if (typeof docRef.data === 'function') {
            const data = docRef.data() || {}
            return { ...data, id: docRef.id }
          }
          if (docRef.id) return { ...docRef }
          return null
        } catch (_) {
          return null
        }
      }).filter(t => t && t.id)
      return sortBy(list, 'updatedAt').reverse()
    },
    // Define the getters for the `TreeNode`
    getSelectedConnector: state => state.selected_connector,
    getCount: state => state.count,
  },
  mutations: {
    FILTER_PARAMS(state, payload) {
      state.params = payload
    },
    UPDATE_LIST(state, payload) {
      const { docs, pagination } = payload

      state.list = (!pagination) ? docs : uniqBy([...state.list, ...docs], 'id')
    },
    UPDATE_TEMPLATES(state, payload) {
      state.templates = payload
    },
    UPDATE_TENANT_TEMPLATES(state, payload) {
      state.tenantTemplates = payload
    },
    UPDATE_FILTERED_LIST(state, payload) {
      state.filteredList = payload
    },
    SET_CURRENT(state, payload) {
      state.current = payload
    },
    STASH_CONNECTORS_MAPPINGS(state, payload) {
      // On empty null and make sure it is always JSON
      state.current.connectorsMappings = isEmpty(payload) ? null : JSON.stringify(payload)
    },
    STASH_CONNECTORS_INSTRUCTIONS(state, payload) {
      // On empty null and make sure it is always JSON
      state.current.connectorsInstructions = isEmpty(payload) ? null : JSON.stringify(payload)
    },
    STASH_CONNECTORS_PERMISSIONS(state, payload) {
      state.current.connectorsPermissions = { ...state.current.connectorsPermissions, ...payload }
    },
    SET_CONNECTORS_PERMISSIONS(state, payload) {
      state.current.connectorsPermissions = payload || {}
    },
    STASH_CONNECTORS_TRIGGER_CONDITIONS(state, payload) {
      state.current.connectorsTriggerConditions = { ...state.current.connectorsTriggerConditions, ...payload }
    },
    SET_CONNECTORS_TRIGGER_CONDITIONS(state, payload) {
      state.current.connectorsTriggerConditions = payload || {}
    },
    STASH_CONNECTOR_OBJECTS(state, payload) {
      state.current.connectorObjects = { ...state.current.connectorObjects, ...payload }
    },
    SET_CONNECTOR_OBJECTS(state, payload) {
      state.current.connectorObjects = payload || {}
    },
    STASH_CONNECTORS_PREFILLS_INSTRUCTIONS(state, payload) {
      // On empty null and make sure it is always JSON
      state.current.connectorsPrefillsInstructions = isEmpty(payload) ? null : JSON.stringify(payload)
    },
    STASH_MAPPING_CONNECTORS_FIELDS(state, payload) {
      state.mapping_connectors_fields = payload
    },
    // Define the mutations for the `TreeNode` changes
    SET_NODE_SELECTED(state, payload) {
      state.node.selected = payload
    },
    SET_NODE_CREATED(state, payload) {
      state.node.created = payload
    },
    SET_NODE_EDITED(state, payload) {
      state.node.edited = payload
    },
    NODE_REMOVE(state, payload) {
      state.node.remove = payload
    },
    SET_SELECTED_CONNECTOR(state, payload) {
      state.selected_connector = payload
    },
    NEW_CREATED_CONNECTOR(state, payload) {
      state.new_created_connector = payload
    },
    SET_COUNT(state, payload) {
      state.count = payload
    },
  },
  actions: {
    /**
     * Fetches all
     *
     * @param {Object}
     * @param {Object} queryOptions
     *
     * @returns {Promise}
     */
    fetchAll({ commit }, payload = {}) {
      return new Promise((resolve, reject) => {
        const { formConfigurations } = tenantCtx()

        const { queryOptions = {}, pagination = false } = payload

        const q = constructQuery(formConfigurations, 'updatedAt', {
          ...queryOptions,
          where: [where('deletedAt', '==', null)],
        })

        getDocs(q)
          .then(async querySnapshot => {
            const revisionsPromises = []
            const docs = []

            // eslint-disable-next-line no-restricted-syntax
            for (const document of querySnapshot.docs) {
              revisionsPromises.push(
                store.dispatch('revisions/fetchById', document.id).then(revision => {
                  docs.push({
                    ...document.data(),
                    updatedBy: revision.user,
                    id: document.id,
                  })
                }),
              )
            }

            await Promise.all(revisionsPromises)

            commit('UPDATE_LIST', { docs, pagination })
            resolve(docs)
          })
          .catch(error => {
            store.dispatch('notify', {
              body: i18n.t('Something went wrong retrieving the Form Configurations'),
              variant: 'danger',
            })

            reject(error)
          })
      })
    },
    /**
     * Fetches document count
     *
     * @param {Object} context - Vuex context
     *
     * @returns {Promise<number>} - Count of matching documents
     */
    fetchCount({ commit }) {
      return new Promise(async (resolve, reject) => {
        try {
          const { formConfigurations } = tenantCtx()

          const q = query(
            formConfigurations,
            where('deletedAt', '==', null),
          )

          const snapshot = await getCountFromServer(q)
          const { count } = snapshot.data()

          commit('SET_COUNT', count)
          resolve(count)
        } catch (error) {
          store.dispatch('notify', {
            body: i18n.t('Something went wrong retrieving the {title}', { title: i18n.t('form configuration') }),
            variant: 'danger',
          })
          reject(error)
        }
      })
    },
    /**
     * Fetches the predefined shared templates/formConfigurations from the _shared tenant context.
     *
     * @param {Object}
     * @param {Object} queryOptions
     *
     * @returns {Promise}
     */
    fetchTemplates({ commit }) {
      return new Promise(resolve => {
        try {
          const { sharedFormConfigurations } = tenantCtx()
          if (!sharedFormConfigurations) {
            commit('UPDATE_TEMPLATES', [])
            return resolve([])
          }

          const q = constructQuery(sharedFormConfigurations, 'updatedAt')

          getDocs(q)
            .then(querySnapshot => {
              commit('UPDATE_TEMPLATES', querySnapshot.docs || [])
              resolve(querySnapshot.docs || [])
            })
            .catch(error => {
              console.debug('fetchTemplates:', error)
              commit('UPDATE_TEMPLATES', [])
              resolve([])
            })
        } catch (err) {
          console.debug('fetchTemplates:', err)
          commit('UPDATE_TEMPLATES', [])
          resolve([])
        }
      })
    },

    /**
     * Fetches form configurations for the current tenant that have type === 'template'.
     *
     * @returns {Promise}
     */
    fetchTenantTemplates({ commit }) {
      return new Promise(resolve => {
        try {
          const { formConfigurations } = tenantCtx()
          if (!formConfigurations) {
            commit('UPDATE_TENANT_TEMPLATES', [])
            return resolve([])
          }

          const q = constructQuery(formConfigurations, 'updatedAt', {
            where: [
              where('deletedAt', '==', null),
              where('type', '==', 'template'),
            ],
          })

          getDocs(q)
            .then(querySnapshot => {
              commit('UPDATE_TENANT_TEMPLATES', querySnapshot.docs || [])
              resolve(querySnapshot.docs || [])
            })
            .catch(error => {
              console.debug('fetchTenantTemplates:', error)
              commit('UPDATE_TENANT_TEMPLATES', [])
              resolve([])
            })
        } catch (err) {
          console.debug('fetchTenantTemplates:', err)
          commit('UPDATE_TENANT_TEMPLATES', [])
          resolve([])
        }
      })
    },

    /**
     * Fetches a document by the given ID
     *
     * @param {Object} id The page ID
     *
     * @returns {Promise} Retrieves all fields in the document as an Object. Returns undefined if the document doesn't exist.
     */
    fetchById({ }, id) {
      return new Promise((resolve, reject) => {
        const { formConfigurations } = tenantCtx()
        const docRef = doc(formConfigurations, id)

        getDoc(docRef)
          .then(docSnapshot => {
            if (!docSnapshot.exists()) {
              reject(new Error('404'))
            }

            if (docSnapshot.data().deletedAt) {
              reject(new Error('404'))
            }

            resolve(docSnapshot.data())
          })
          .catch(error => {
            store.dispatch('notify', {
              body: i18n.t('Something went wrong retrieving the {title}', { title: 'Form Configuration' }),
              variant: 'danger',
            })

            reject(error)
          })
      })
    },

    /**
     * Fetches a document by the current item ID
     *
     * @param {string} id The current page ID
     */
    fetchCurrent({ dispatch, commit }, id) {
      return new Promise((resolve, reject) => {
        dispatch('fetchById', id)
          .then(res => {
            commit('SET_CURRENT', res)
            resolve(res)
          })
          .catch(error => reject(error))
      })
    },

    /**
     * Fetches on search query
     *
     * @param {Object}
     * @param {Object} queryOptions
     *
     * @returns {Promise}
     */
    fetchFiltered({ commit }, queryOptions) {
      return new Promise((resolve, reject) => {
        const { formConfigurations } = tenantCtx()

        const q = constructQuery(formConfigurations, 'updatedAt', {
          ...queryOptions,
          where: [...queryOptions.where, where('deletedAt', '==', null)],
        })

        getDocs(q)
          .then(querySnapshot => {
            commit('UPDATE_FILTERED_LIST', querySnapshot.docs)
            resolve(querySnapshot.docs)
          })
          .catch(error => {
            store.dispatch('notify', {
              body: i18n.t('Something went wrong retrieving the form configurations'),
              variant: 'danger',
            })

            reject(error)
          })
      })
    },

    /**
     * resets the filter list
     *
     * @param {Object}
     *
     * @returns {Promise}
     */
    resetFiltered({ commit }) {
      return new Promise(resolve => {
        commit('UPDATE_FILTERED_LIST', [])
        resolve()
      })
    },

    /**
     * Creates a document by the given payload
     *
     * @param {Object} payload
     *
     * @returns {Promise}
     */
    create({ }, payload) {
      return new Promise((resolve, reject) => {
        if (!allowed(FEATURES.FORM_CONFIGURATIONS_QUOTA)) {
          reject()

          store.dispatch('notify', {
            body: i18n.t('You have reached your {title} limit', { title: i18n.t('form configurations') }),
            variant: 'danger',
          })

          return
        }

        const { formConfigurations } = tenantCtx()
        const docRef = doc(formConfigurations)

        setDoc(docRef, { ...payload, id: docRef.id, deletedAt: null })
          .then(() => {
            store.dispatch('revisions/create', {
              event: RevisionEventType.CREATE_FORM_CONFIGURATIONS,
              id: docRef.id,
              newValue: payload,
              previousValue: {},
            })
            store.dispatch('notify', { title: i18n.t('Great!'), body: i18n.t('A new {title} has been created 🚀', { title: i18n.t('form configuration') }) })
            resolve(docRef)
          })
          .catch(error => {
            store.dispatch('notify', {
              body: i18n.t('Something went wrong creating the Form Configuration'),
              variant: 'danger',
            })

            reject(error)
          })
      })
    },

    /**
     * Updates a document by the given payload
     *
     * @param {Object} payload
     *
     * @returns {Promise}
     */
    update({ dispatch }, { value, previousValue }) {
      return new Promise((resolve, reject) => {
        const { formConfigurations } = tenantCtx()
        const docRef = doc(formConfigurations, value.id)
        const { tags: _, ...valueWithoutTags } = value

        updateDoc(docRef, valueWithoutTags)
          .then(() => {
            store.dispatch('revisions/create', {
              event: RevisionEventType.UPDATE_FORM_CONFIGURATIONS,
              id: value.id,
              newValue: { ...valueWithoutTags },
              previousValue,
            })
            store.dispatch('notify', { body: i18n.t('Form Configuration has been updated') })

            // Wait for fetchCurrent to complete before resolving so the
            // caller knows state.current has been refreshed from Firestore.
            // Without this, the Mapping/Instructions components reinitialise
            // from stale state and root-level fields disappear from the UI.
            return dispatch('fetchCurrent', value.id)
          })
          .then(() => {
            resolve(true)
          })
          .catch(error => {
            store.dispatch('notify', {
              body: i18n.t('Something went wrong updating the Form Configuration'),
              variant: 'danger',
            })

            reject(error)
          })
      })
    },

    /**
     * Sets the form configuration document type to 'template' in Firestore.
     *
     * @param {string} id - Document ID
     *
     * @returns {Promise}
     */
    setAsTemplate({ dispatch }, id) {
      return new Promise((resolve, reject) => {
        const { formConfigurations } = tenantCtx()
        const docRef = doc(formConfigurations, id)

        updateDoc(docRef, { type: 'template', updatedAt: getUnix() })
          .then(() => {
            store.dispatch('notify', { body: i18n.t('Saved as template') })
            dispatch('fetchCurrent', id)
            resolve()
          })
          .catch(error => {
            store.dispatch('notify', {
              body: i18n.t('Something went wrong saving as template'),
              variant: 'danger',
            })
            reject(error)
          })
      })
    },

    /**
     * Unsets the form configuration document type so it is no longer treated as a template.
     *
     * @param {string} id - Document ID
     *
     * @returns {Promise}
     */
    unsetTemplate({ dispatch }, id) {
      return new Promise((resolve, reject) => {
        const { formConfigurations } = tenantCtx()
        const docRef = doc(formConfigurations, id)

        updateDoc(docRef, { type: null, updatedAt: getUnix() })
          .then(() => {
            store.dispatch('notify', { body: i18n.t('Removed from templates') })
            dispatch('fetchCurrent', id)
            resolve()
          })
          .catch(error => {
            store.dispatch('notify', {
              body: i18n.t('Something went wrong removing from templates'),
              variant: 'danger',
            })
            reject(error)
          })
      })
    },

    /**
     * Soft deletes a document
     *
     * @param {Object} payload
     *
     * @returns {Promise}
     */
    softDelete({ }, payload) {
      return new Promise((resolve, reject) => {
        const { formConfigurations } = tenantCtx()
        const docRef = doc(formConfigurations, payload.id)

        updateDoc(docRef, {
          deletedAt: getUnix(),
        })
          .then(() => {
            store.dispatch('revisions/create', {
              event: RevisionEventType.DELETE_FORM_CONFIGURATIONS,
              id: payload.id,
              newValue: {},
              previousValue: payload,
            })
            resolve(true)
          })
          .catch(error => {
            reject(error)
          })
      })
    },

    /**
     * Duplicates a document
     *
     * @param {Object}
     * @param {string} id The id of the document to duplicate
     *
     * @returns {Promise}
     */
    duplicate({ }, id) {
      return new Promise(async (resolve, reject) => {
        if (!allowed(FEATURES.FORM_CONFIGURATIONS_QUOTA)) {
          reject()

          store.dispatch('notify', {
            body: i18n.t('You have reached your {title} limit', { title: i18n.t('form configurations') }),
            variant: 'danger',
          })

          return
        }

        try {
          const timestamp = getUnix()
          const { formConfigurations } = tenantCtx()

          const docRef = doc(formConfigurations, id)
          const docSnapshot = await getDoc(docRef)
          const docSnapshotData = docSnapshot.data()

          const newDocRef = doc(formConfigurations)

          const data = {
            ...docSnapshotData,
            id: newDocRef.id,
            description: `${docSnapshotData.description} ${timestamp} - ${i18n.t('COPY')}`,
            // Ensure duplicated configurations are never treated as templates.
            type: null,
            // Set all timestamp properties.
            createdAt: timestamp,
            deletedAt: null,
            updatedAt: null,
          }

          await setDoc(newDocRef, data)

          await store.dispatch('revisions/create', {
            event: RevisionEventType.DUPLICATE_FORM_CONFIGURATIONS,
            id: newDocRef.id,
            newValue: data,
            previousValue: {},
          })

          await router.push({
            name: 'form-configurations-configurations-edit',
            params: { id: newDocRef.id },
          })
          await store.dispatch('notify', {
            title: i18n.t('Great!'),
            body: i18n.t('A new {title} has been duplicated 🚀', { title: i18n.t('form configuration') }),
          })
          resolve(newDocRef)
        } catch (error) {
          store.dispatch('notify', {
            body: i18n.t('Something went wrong duplicating the {title}', { title: i18n.t('form configuration') }),
            variant: 'danger',
          })

          reject(error)
        }
      })
    },
  },
}
